/**
 * THE CANONICAL PRODUCT VOCABULARY.
 *
 * Source: REQUIREMENTS_PROJECT_CONTEXT.md §6. That document is the authority on every
 * term, action verb, lifecycle state and role in the product, and it says so:
 * "Every document in this set uses these terms and only these terms."
 *
 * This file is the ONLY place these values are defined. Do not re-declare any of them
 * in api/, web/ or worker/ — a second definition is how "three competing definitions of
 * what makes a tool useful" happened in the first place (REVIEW_CROSS_DOC_COMPATIBILITY.md
 * C-03). Import from here.
 *
 * Changing anything in this file is a PRODUCT change, not a refactor. It needs the product
 * owner (see .github/CODEOWNERS).
 */
// ─────────────────────────────────────────────────────────────────────────────
// The four canonical actions (PC §6.2)
//
// These are the ONLY actions the product counts. AE-F14: "Record only the four
// activity types. Anything else is rejected rather than recorded."
//
// A rating is NOT a fifth. It is a feedback record owned by FB-F19, deliberately
// listed separately, and the activity record holds exactly four (AE §5).
// Learning progress is NOT a fifth either — it is a learning record (LH-F13).
// ─────────────────────────────────────────────────────────────────────────────
export const ACTIVITY_TYPES = ['view', 'save', 'adopt', 'like'];
/**
 * What each action feeds. Encoded so the firewall is checkable rather than remembered.
 *
 * - `adopt` is the single measure of demonstrated usefulness, and the ONLY activity
 *   measure management reporting reads (PC §6.2, FB §7.2).
 * - `like` is the multiplier in the weekly leaderboard score (RT-F02) and reaches the
 *   management dashboard in NO form (MD-NFR10).
 * - `save` is a private bookmark and feeds NOTHING, ANYWHERE (FB-F02).
 * - `view` is aggregate-only and feeds the neglect indicator (CAT-F22a).
 */
export const ACTIVITY_FEEDS = {
    view: { publicCount: false, leaderboard: false, managementDashboard: true, neglect: true },
    save: { publicCount: false, leaderboard: false, managementDashboard: false, neglect: false },
    adopt: { publicCount: true, leaderboard: true, managementDashboard: true, neglect: true },
    like: { publicCount: true, leaderboard: true, managementDashboard: false, neglect: false },
};
/** Words deleted from the whole product (PC §6.2). Never use these in code or copy. */
export const RETIRED_WORDS = {
    install: 'adopt',
    download: 'adopt',
    'get a copy': 'adopt',
    pin: 'save',
    bookmark: 'save',
    favourite: 'save',
    upvote: 'like',
};
// ─────────────────────────────────────────────────────────────────────────────
// Lifecycle: three INDEPENDENT axes (PC §6.3)
//
// A tool has a value on each axis at all times. A tool can be Approved AND reviewed
// AND still Broken. Never collapse these into one status field — CAT-F18 is explicit
// that health is "separate from publication and review status".
// ─────────────────────────────────────────────────────────────────────────────
export const PUBLICATION_STATUSES = [
    'Draft',
    'Pending',
    'Approved',
    'Rejected',
    'Withdrawn',
];
/** CAT-F16 / PC §6.3: Approved is the ONLY status the catalogue exposes as visible, and
 *  only when the record also passes the completeness gate (CAT-F23). */
export const VISIBLE_PUBLICATION_STATUS = 'Approved';
export const HEALTH_STATES = ['Healthy', 'Unmaintained', 'Broken'];
export const REVIEW_MARKER_STATES = ['none', 'current', 'lapsed'];
/**
 * Three words that are NOT interchangeable (PC §6.3, CAT §11.2). Getting these wrong
 * was a real defect in the set — three unaligned windows meaning three different things.
 *
 *   neglected  = nobody wants it   — no views AND no adoptions for 90 days.
 *                An INDICATOR. Changes no status and no health state. Never shown to
 *                end users; reported to the platform owner and the dashboard.
 *   stale      = nobody maintains it — no update for 12 months. A TRIGGER for Unmaintained.
 *   ownerless  = the builder has left and nobody has taken it on. The other TRIGGER.
 *   Broken     = it is known not to work. A health STATE.
 */
export const LIFECYCLE_WORD_MEANINGS = {
    neglected: { kind: 'indicator', changesState: false },
    stale: { kind: 'trigger', leadsTo: 'Unmaintained' },
    ownerless: { kind: 'trigger', leadsTo: 'Unmaintained' },
    Unmaintained: { kind: 'healthState' },
    Broken: { kind: 'healthState' },
};
// ─────────────────────────────────────────────────────────────────────────────
// Classification: ONE governed axis (PC §6.4, CAT-F05)
//
// Exactly one primary category per tool, from a governed list the platform owner can
// change without a deploy (CAT-F06). These are SEED VALUES ONLY — the live list is the
// `category` table. Never hard-code a check against these, and never write a fixed count
// (e.g. "six"/"four"/"three") in user-facing copy: the owner may add or retire categories
// (see IMPLEMENTATION_PLAN.md §23-B8, and the three-category decision in
// REQUIREMENTS_TOOL_CATALOGUE.md §8).
//
// "Spreadsheets" was seeded by 0015 and is RETIRED by 0022, so it is no longer listed here. It is
// not deleted from the table: `CAT-F06` makes retirement the governed mechanism, and a tool may
// still reference the row. A spreadsheet-based tool is classified by what it DOES — usually
// Automations or Apps — and the artefact is identified by a tag (`excel`, `macro`, `template`).
// ─────────────────────────────────────────────────────────────────────────────
export const SEED_PRIMARY_CATEGORIES = ['Automations', 'Apps', 'AI & Agents'];
/** CAT-F07: product names are TAGS, never categories. Rejected at category creation. */
export const FORBIDDEN_AS_CATEGORY = [
    'power automate',
    'power apps',
    'power bi',
    'copilot',
    'excel',
    'word',
    'powerpoint',
    'sharepoint',
    'teams',
    'dataverse',
];
/**
 * Artefact type decides what an adopter is HANDED (AE-F04). It is a handover attribute,
 * NOT a classification — CAT §5 is explicit, and the old "tool type" field it replaced
 * was removed from the set (UP-F04a, withdrawn).
 *
 * `dataverse_flow` is DELIBERATELY ABSENT. The spike proved a Dataverse flow-reference
 * plus clone capability, and the requirements define exactly three artefact types, so
 * adding a fourth is a requirements change, not an implementation detail.
 * See IMPLEMENTATION_PLAN.md §23-A9 and FEATURE_DATAVERSE_ARTEFACT.
 */
export const ARTEFACT_TYPES = ['file', 'prompt', 'link'];
/**
 * How submissions are gated before publication (UP-F20). The PLATFORM OWNER sets this from the
 * administration surface with no deploy (UP-F24); it — not the submitter — decides routing:
 *
 *   manual_review   = every submission is held Pending and enters the reviewer queue; a reviewer's
 *                     sign-off publishes it (UP-F22). The safe default: nothing is published unseen.
 *   self_moderated  = a submission that passes every automated pre-check publishes as Approved with
 *                     no human step (UP-F21).
 *
 * Stored as the `moderation_mode` platform_setting (seeded `"manual_review"` in 0015). These strings
 * are the persisted values — never re-spell them. An unrecognised stored value falls back to the
 * default, so a typo in the admin surface holds submissions rather than publishing them unseen.
 */
export const MODERATION_MODES = ['manual_review', 'self_moderated'];
export const DEFAULT_MODERATION_MODE = 'manual_review';
// ─────────────────────────────────────────────────────────────────────────────
// People: what is APPOINTED, and what is SELF-DECLARED (PC §6.5, §6.6)
//
// PC-F01, the rule that exists because a naming collision was a security defect:
// "No self-assessment, self-declaration or automatic progression ever grants a
// privileged role, elevated visibility, or access to restricted content."
// ─────────────────────────────────────────────────────────────────────────────
/** APPOINTED. These are the ONLY values that gate anything (UR-NFR08).
 *  Resolved from a live appointment at the moment of use, never from a stored flag on
 *  the person, and never inferred from anything self-declared (UR-F08, UR-NFR02). */
export const APPOINTED_ROLES = [
    'Reviewer',
    'ChampionRole',
    'SWATMember',
    'Manager',
    'PlatformOwner',
];
/** SELF-DECLARED. Grants NOTHING, ever (UR-F14, UR-NFR08, PC-F01).
 *  The top rung is `Advanced`, NOT "Champion" — a self-assessed level must never share
 *  a name with an appointed role. LH §1 records that the rename was the cheapest fix
 *  for what would otherwise have been a real security defect. */
export const COMPETENCY_LEVELS = ['Beginner', 'Practitioner', 'Advanced'];
/** SELF-DECLARED. Shapes what the learning hub suggests. Grants nothing (UR-F29). */
export const MAKER_TYPES = ['Business user', 'Maker', 'Pro-developer'];
/**
 * Everything a person can hold that confers NO access, NO privilege and NO visibility
 * (UR-NFR08, PR-NFR08, PC-F01).
 *
 * None of these may appear in authz/policy.ts, in a permission check, in a filter, in a
 * metric, or in an ORDER BY. There is a test that asserts it.
 */
export const GRANTS_NOTHING = [
    'CompetencyLevel',
    'MakerType',
    'BaselineLevel',
    'Badges',
    'FollowerCount',
    'SkillsHave',
    'SkillsWanted',
    'Certifications',
    'Team',
    'RoleLabel',
    'SupportChampion',
];
// ─────────────────────────────────────────────────────────────────────────────
// Support requests (FB-F16) — exactly four statuses
// ─────────────────────────────────────────────────────────────────────────────
export const SUPPORT_REQUEST_STATUSES = ['Open', 'In progress', 'Resolved', 'Closed'];
// ─────────────────────────────────────────────────────────────────────────────
// Notifications (NT §7) — exactly EIGHT kinds, and no ninth.
//
// NT-F02: "Support only the eight notifications in §7. Anything else is rejected rather
// than sent, and adding a ninth requires changing this document."
// ─────────────────────────────────────────────────────────────────────────────
export const NOTIFICATION_KINDS = {
    1: { name: 'tool_published', recipient: 'community_channel', optOut: 'channel_mute' },
    2: { name: 'adoption_milestone', recipient: 'builder', optOut: 'per_person' },
    3: { name: 'comment_or_issue_on_your_tool', recipient: 'builder', optOut: 'per_person' },
    4: { name: 'submission_status_change', recipient: 'submitter', optOut: 'none' },
    5: { name: 'needs_attention', recipient: 'owner_and_swat', optOut: 'none' },
    6: { name: 'reply_to_your_post', recipient: 'poster', optOut: 'per_person' },
    7: { name: 'outcome_on_your_content', recipient: 'poster_or_raiser', optOut: 'none' },
    8: { name: 'first_top5_entry', recipient: 'builder', optOut: 'per_person' },
};
/** NT-F10: only these four can be turned off. NT-F11: the rest state why they cannot. */
export const OPT_OUT_ABLE_NOTIFICATIONS = [
    2, 3, 6, 8,
];
// ─────────────────────────────────────────────────────────────────────────────
// Community hub (CH-F01) — SIX FIXED channels. Employees cannot create or delete one.
// `readRole`/`postRole` of null means everyone.
// ─────────────────────────────────────────────────────────────────────────────
export const CHANNELS = [
    { id: 1, slug: 'questions', name: 'Questions', readRole: null, postRole: null },
    { id: 2, slug: 'promoted-tools', name: 'Promoted Tools', readRole: null, postRole: null },
    {
        id: 3,
        slug: 'swat-suggestions',
        name: 'SWAT Team Suggestions',
        readRole: null,
        postRole: null,
    },
    {
        id: 4,
        slug: 'champion-announcements',
        name: 'Champion Announcements',
        readRole: null,
        postRole: 'ChampionRole',
    },
    {
        id: 5,
        slug: 'champions-only',
        name: 'Champions Only',
        readRole: 'ChampionRole',
        postRole: 'ChampionRole',
    },
    {
        id: 6,
        slug: 'swat-only',
        name: 'SWAT Team Only',
        readRole: 'SWATMember',
        postRole: 'SWATMember',
    },
];
/** CH-F09: nesting goes to three levels BELOW the opening post, and a reply to a
 *  third-level post attaches AT the third level rather than creating a fourth. */
export const MAX_POST_DEPTH = 3;
// ─────────────────────────────────────────────────────────────────────────────
// Primary navigation (PC-F10) — exactly these seven, in this order.
// Management dashboard and Administration are appended only for holders (PC-F11).
// PC-F14: a named future capability appears NOWHERE in the interface until it exists.
// ─────────────────────────────────────────────────────────────────────────────
export const PRIMARY_NAV = [
    { label: 'Home', path: '/' },
    { label: 'Tools', path: '/tools' },
    { label: 'Share a tool', path: '/share' },
    { label: 'Learning', path: '/learning' },
    { label: 'Community', path: '/community' },
    { label: 'My profile', path: '/me' },
    { label: 'Notifications', path: '/notifications' },
];
/**
 * `PC §10 group C` describes ONE administration surface for the platform owner, but the
 * implementation had drifted into four separate entries here (Administration, Review queue,
 * Moderation queue, Support & issues) — never consolidated. Collapsed to one "Admin Tools" entry,
 * any-of Reviewer/ChampionRole/SWATMember/PlatformOwner, opening a role-scoped landing page at
 * /admin that links out to the individual queues (still directly routable at their own URLs).
 * Management dashboard stays fully separate — a different, aggregate-reporting audience (PC-F11).
 * See ALIGNMENT_CHANGELOG.md's entry closing gap G-07 for the record of this decision.
 */
export const ROLE_GATED_NAV = [
    { label: 'Management dashboard', path: '/dashboard', requires: ['Manager'] },
    {
        label: 'Admin Tools',
        path: '/admin',
        requires: ['Reviewer', 'ChampionRole', 'SWATMember', 'PlatformOwner'],
    },
];
//# sourceMappingURL=vocabulary.js.map